'use strict';

/**
 * Pict-Provider-RunAPI
 * --------------------
 * Browser client for the toolchain run surface (Vision 43). One read (GET /1.0/RunQueue) backs the
 * Runs page: the runs in flight, their snapshotted steps, and the evidence a node attached to each
 * step. startRun() queues a Run (POST /1.0/Run/Start) that a node claims and executes; consent()
 * answers a gated step (POST /1.0/RunStep/:id/Consent).
 *
 * The page never executes anything and holds no credentials, exactly like the Releases page: it
 * writes rows a node picks up. The one thing it does that no node can is answer a gate.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'RunAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderRunAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Runs)
		{
			this.fable.AppData.Runs = { Loaded: false, LoadError: null, Runs: [], CanApprove: false, Toolchains: [] };
		}
	}

	loadRuns()
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.Runs;
		return libFrameworkClient.getJSON(this.fable, this.routePrefix + 'RunQueue')
			.then((pBody) =>
			{
				tmpStore.Runs = (pBody && pBody.Runs) || [];
				tmpStore.CanApprove = !!(pBody && pBody.CanApprove);
				tmpStore.Loaded = true;
				tmpStore.LoadError = null;
				return pBody;
			})
			.catch((pError) => { tmpStore.LoadError = pError.message; tmpStore.Loaded = true; return null; });
	}

	// The toolchains this tenant can run: platform built-ins, shadowed by any the tenant has adopted
	// or authored. A catalog read failing must not blank the runs the page already has, so it parks
	// its own error and resolves.
	loadToolchains()
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.Runs;
		return libFrameworkClient.getJSON(this.fable, this.routePrefix + 'Toolchains/Available')
			.then((pBody) =>
			{
				tmpStore.Toolchains = (pBody && pBody.Toolchains) || [];
				tmpStore.CatalogError = null;
				return pBody;
			})
			.catch((pError) => { tmpStore.CatalogError = pError.message; tmpStore.Toolchains = []; return null; });
	}

	// Queue a run of a toolchain for a node to claim. pToolchainKey names a tenant toolchain or a
	// platform built-in (adopted into the tenant on first use).
	//
	// The server refuses with a 409 while a run of the same toolchain is still in flight, and a run
	// parked at AwaitingConsent counts as in flight. Force plus Reason is the operator override; it
	// needs content.approve and lands a run.forced beat on the new run's timeline.
	startRun(pToolchainKey, pOptions)
	{
		let tmpOptions = pOptions || {};
		return libFrameworkClient.postJSON(this.fable, this.routePrefix + 'Run/Start',
		{
			ToolchainKey: pToolchainKey,
			IDWorkItem: Number(tmpOptions.IDWorkItem) || 0,
			Name: tmpOptions.Name || '',
			Environment: tmpOptions.Environment || '',
			Force: !!tmpOptions.Force,
			Reason: tmpOptions.Reason || ''
		});
	}

	// Answer a gated step. pDecision is 'Approve' or 'Reject'; the server gates this on content.approve.
	consent(pIDRunStep, pDecision, pNote)
	{
		return libFrameworkClient.postJSON(this.fable, this.routePrefix + 'RunStep/' + pIDRunStep + '/Consent',
			{ Decision: pDecision, Note: pNote || '' });
	}

	// Stop a run going wrong without SSH-ing into the node (Work Item #451). This only marks the run
	// CancelRequested; the node notices on its own next heartbeat and stops itself. The server gates
	// this on content.approve and answers a no-op (not an error) when nothing is actually running.
	cancel(pIDRun, pReason)
	{
		return libFrameworkClient.postJSON(this.fable, this.routePrefix + 'Run/' + pIDRun + '/Cancel', { Reason: pReason || '' });
	}
}

module.exports = PictProviderRunAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
